import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { LangSwitcher } from '../../components/LangSwitcher/LangSwitcher';
import marginLogo from '../../assets/vectorlogo.png';
import './Influencer.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Sale {
  id: number;
  createdAt: string;
  paymentType: string;
  username: string;
}

interface InfluencerMe {
  tiktok: string;
  instagram: string;
  promoCode: { code: string; discountPercent: number; expiresAt: string } | null;
  rewardPerSaleKzt: number;
  sales: Sale[];
  count: number;
  totalKzt: number;
}

// Курсы захардкожены — этого достаточно для inline-конвертации награды
// 5000 KZT в выбранную валюту. Источник: округлённые курсы за 2026 Q2.
// Обновлять руками; точность ±5% не критична для дашборда инфлюенсера.
const CURRENCY_RATES: Record<string, { perKzt: number; label: string; symbol: string }> = {
  USD: { perKzt: 1 / 470, label: 'USD', symbol: '$' },
  KZT: { perKzt: 1, label: 'KZT', symbol: '₸' },
  RUB: { perKzt: 1 / 5.4, label: 'RUB', symbol: '₽' },
  EUR: { perKzt: 1 / 520, label: 'EUR', symbol: '€' },
};
const CURRENCY_ORDER = ['USD', 'KZT', 'RUB', 'EUR'];

// Дефолт — USD; язык по умолчанию = русский (про русский занимается i18n,
// у нас тут только валюта).
const DEFAULT_CURRENCY = 'USD';

// Локальные строки — не тащим i18n-namespace ради одной страницы. Локалей
// две: ru (default) + en. Остальные языки fallback'ятся на ru.
const STRINGS: Record<string, Record<string, string>> = {
  ru: {
    title: 'Ваш заработок',
    sub: 'Кабинет инфлюенсера',
    total: 'Всего заработано',
    count_one: '{n} продажа',
    count_few: '{n} продажи',
    count_many: '{n} продаж',
    promo: 'Промокод',
    tiktok: 'TikTok',
    instagram: 'Instagram',
    table_empty: 'Пока нет ни одной продажи по вашему промокоду.',
    book: 'Книга Lovestoria',
    paytype_prepayment: 'Предоплата',
    paytype_full_payment: 'Полная оплата',
    paytype_postpayment: 'Постоплата',
    paytype_express_print: 'Экспресс печать',
    paytype_express_edit: 'Уск. редактура',
    paytype_urep: 'УРЭП',
    paytype_delivery: 'Доставка',
    paytype_additional: 'Доп.',
    logout: 'Выйти',
    today: 'сегодня',
    discount_suffix: '· скидка {n}%',
  },
  en: {
    title: 'Your earnings',
    sub: 'Influencer dashboard',
    total: 'Total earned',
    count_one: '{n} sale',
    count_few: '{n} sales',
    count_many: '{n} sales',
    promo: 'Promo code',
    tiktok: 'TikTok',
    instagram: 'Instagram',
    table_empty: 'No sales yet from your promo code.',
    book: 'Lovestoria Book',
    paytype_prepayment: 'Prepayment',
    paytype_full_payment: 'Full payment',
    paytype_postpayment: 'Postpayment',
    paytype_express_print: 'Express print',
    paytype_express_edit: 'Express edit',
    paytype_urep: 'UREP',
    paytype_delivery: 'Delivery',
    paytype_additional: 'Extra',
    logout: 'Log out',
    today: 'today',
    discount_suffix: '· {n}% off',
  },
};

function tr(lang: string, key: string, vars?: Record<string, string | number>): string {
  const dict = STRINGS[lang] ?? STRINGS.ru;
  let s = dict[key] ?? STRINGS.ru[key] ?? key;
  if (vars) for (const k of Object.keys(vars)) s = s.replaceAll(`{${k}}`, String(vars[k]));
  return s;
}

function pluralRu(n: number, one: string, few: string, many: string): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return few;
  return many;
}

function formatCountLabel(lang: string, n: number): string {
  if (lang === 'ru') {
    return pluralRu(n, tr(lang, 'count_one', { n }), tr(lang, 'count_few', { n }), tr(lang, 'count_many', { n }));
  }
  return n === 1 ? tr(lang, 'count_one', { n }) : tr(lang, 'count_many', { n });
}

function formatDate(iso: string, lang: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (sameDay) return `${tr(lang, 'today')} ${hh}:${mm}`;
  const dd = String(d.getDate()).padStart(2, '0');
  const mo = String(d.getMonth() + 1).padStart(2, '0');
  return `${dd}.${mo}, ${hh}:${mm}`;
}

function formatMoney(amountKzt: number, currency: string): string {
  const rate = CURRENCY_RATES[currency] ?? CURRENCY_RATES.KZT;
  const converted = amountKzt * rate.perKzt;
  // KZT/RUB — без копеек, остальные — с двумя знаками.
  const formatted =
    currency === 'KZT' || currency === 'RUB'
      ? Math.round(converted).toLocaleString('ru-RU')
      : converted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return `${formatted} ${rate.symbol}`;
}

export const Influencer: React.FC = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const lang = STRINGS[i18n.language] ? i18n.language : 'ru';

  const [data, setData] = useState<InfluencerMe | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currency, setCurrency] = useState<string>(() => {
    return localStorage.getItem('influencer_currency') || DEFAULT_CURRENCY;
  });

  useEffect(() => {
    localStorage.setItem('influencer_currency', currency);
  }, [currency]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { replace: true });
      return;
    }
    (async () => {
      try {
        const r = await axios.get<InfluencerMe>(`${API_URL}/influencers/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setData(r.data);
      } catch (err: any) {
        if (err?.response?.status === 401) {
          localStorage.removeItem('token');
          navigate('/login', { replace: true });
          return;
        }
        if (err?.response?.status === 403) {
          // Не инфлюенсер — выкидываем на обычный дашборд.
          navigate('/forms', { replace: true });
          return;
        }
        setError('failed');
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/login');
  };

  const totalLabel = useMemo(() => {
    if (!data) return '';
    return formatMoney(data.totalKzt, currency);
  }, [data, currency]);

  const perSaleLabel = useMemo(() => {
    if (!data) return '';
    return formatMoney(data.rewardPerSaleKzt, currency);
  }, [data, currency]);

  if (loading) return <div className="infl-loading">…</div>;
  if (error || !data) return <div className="infl-loading">—</div>;

  return (
    <div className="infl">
      <div className="infl__bg" />

      <header className="infl__top">
        <div className="infl__brand">
          <img src={marginLogo} alt="lovestoria" className="infl__brand-img" />
        </div>
        <div className="infl__top-ctl">
          <select
            className="infl__select"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
          >
            {CURRENCY_ORDER.map((c) => (
              <option key={c} value={c}>{CURRENCY_RATES[c].label}</option>
            ))}
          </select>
          <LangSwitcher tone="dark" />
          <button className="infl__logout" onClick={handleLogout}>
            {tr(lang, 'logout')}
          </button>
        </div>
      </header>

      <main className="infl__main">
        <div className="infl__sub">{tr(lang, 'sub')}</div>
        <h1 className="infl__title">{tr(lang, 'title')}</h1>

        <section className="infl__hero">
          <div className="infl__total">{totalLabel}</div>
          <div className="infl__count">{formatCountLabel(lang, data.count)}</div>
        </section>

        <section className="infl__meta">
          {data.promoCode && (
            <div className="infl__meta-row">
              <span className="infl__meta-label">{tr(lang, 'promo')}</span>
              <span className="infl__meta-value infl__meta-value--code">
                {data.promoCode.code}
                <span className="infl__meta-suffix">
                  {' '}{tr(lang, 'discount_suffix', { n: data.promoCode.discountPercent })}
                </span>
              </span>
            </div>
          )}
          {data.tiktok && (
            <div className="infl__meta-row">
              <span className="infl__meta-label">{tr(lang, 'tiktok')}</span>
              <span className="infl__meta-value">{data.tiktok}</span>
            </div>
          )}
          {data.instagram && (
            <div className="infl__meta-row">
              <span className="infl__meta-label">{tr(lang, 'instagram')}</span>
              <span className="infl__meta-value">{data.instagram}</span>
            </div>
          )}
        </section>

        <section className="infl__table-wrap">
          {data.sales.length === 0 ? (
            <div className="infl__empty">{tr(lang, 'table_empty')}</div>
          ) : (
            <table className="infl__table">
              <tbody>
                {data.sales.map((s) => {
                  const ptKey = `paytype_${s.paymentType}`;
                  const ptLabel = STRINGS[lang]?.[ptKey] ?? STRINGS.ru[ptKey] ?? s.paymentType;
                  return (
                    <tr key={s.id}>
                      <td className="infl__td-date">{formatDate(s.createdAt, lang)}</td>
                      <td className="infl__td-desc">
                        <span className="infl__td-book">{tr(lang, 'book')}</span>
                        <span className="infl__td-paytype">{ptLabel}</span>
                      </td>
                      <td className="infl__td-amount">+ {perSaleLabel}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </section>
      </main>
    </div>
  );
};

export default Influencer;
